/* eslint-disable no-useless-escape */
import moment from 'moment'
import axios from 'axios'
import Config from '../constants/endpoints.js'

import DNS_URLS from '../constants/dnsUrls'

import { apiAutocomplete, shorten } from '../api'
import { getStaticImage } from './statics'
import { rawTimeZones } from '@vvo/tzdb'
import { I18n } from 'react-redux-i18n'
import { Interval, DateTime } from 'luxon'

const QRCode = require('qrcode')

const formatDate = {
  full: 'DD-MM-YYYY HH:mm:ss',
  fullNotSeconds: 'DD-MM-YYYY HH:mm',
  date: 'DD-MM-YYYY',
}

const httpsUrlRegex = /https?:\/\/(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&\/\/=]*)/i

export function getDateTime(date, format) {
  if (!date || !format) return ''

  return moment(date).format(formatDate[format])
}

export function getShortDate(date) {
  if (typeof date === 'string') {
    date = new Date(/[0-9]{4}-[0-9]{2}-[0-9]{2}/.exec(date)[0])
  }

  return `${date.getDate()} ${date.getMonth() + 1}`
}

export function getDateTimeFromNow(date, locale = 'en') {
  if (!date) return ''

  return moment(date).locale(checkLocale(locale)).fromNow()
}

export function getUrlParam(name) {
  var url = window.location.href
  name = name.replace(/[\[\]]/g, '\\$&')
  var regex = new RegExp('[?&]' + name + '(=([^&#]*)|&|#|$)')
  var results = regex.exec(url)
  if (!results) return null
  if (!results[2]) return ''
  return decodeURIComponent(results[2].replace(/\+/g, ' '))
}

export function checkLocale(locale) {
  if (!locale) return 'es'
  const locales = ['es', 'en']
  locale = locale.split('_')[0].toLowerCase()
  if (locales.indexOf(locale) === 0) return 'es'
  return locale
}

export function isValidEmail(email) {
  const re = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
  return re.test(email)
}

export function isValidText(text) {
  const re = /^(?!\s*$).+/
  return re.test(text)
}

export function isValidUrl(text) {
  const regex = /^(https?|ftp|torrent|image|irc):\/\/(-\.)?([^\s\/?\.#-]+\.?)+(\/[^\s]*)?$/i
  return text.match(regex)
}

export function isValidHttpsUrl(text) {
  return httpsUrlRegex.test(text)
}

export function notBlankSpaceStartAndEnd(text) {
  return !/^\s+|\s+$/.test(text)
}

export function isValidPassword(password) {
  const errors = []

  if (/\s/.test(password)) errors.push('error_space')
  if (!/\d/.test(password)) errors.push('error_string')
  if (!/[a-zA-Z]/g.test(password)) errors.push('error_string')
  if (password.length < 8) errors.push('error_min_length')

  return [...new Set(errors)]
}

export function isValidPhone(phone) {
  const re = /^\d*$/
  return re.test(phone)
}

export function isValidNif(nif) {
  const re = /\s/
  return !re.test(nif)
}

/**
 * Returns an array of select options from the flights locations api.
 *
 * @param {String} input Text string to search for
 */
export function getFlightLocations(input) {
  let options = []

  if (input.length < 3) return new Promise((resolve) => resolve(options))

  return apiAutocomplete
    .locations({ query: input, lang: 'es' })
    .then(({ data, status }) => {
      let options = []
      if (status === 200) {
        options = data.result.map((r) => ({
          value: r.iata,
          label: `${r.showname} (${r.iata})`,
          countryCode: r.country_id,
        }))
      }

      return options
    })
    .catch((err) => console.error(err))
}

/**
 * Returns an array of select options from the flights locations api.
 *
 * @param {String} code Text string to search for
 */
export function getFlightLocationsPlugin(code) {
  let options = []

  if (code.length < 3) return new Promise((resolve) => resolve(options))

  return apiAutocomplete
    .locations({ query: code, lang: 'es' })
    .then(({ data, status }) => {
      let options = []
      if (status === 200) {
        options = data.result.map((r) => ({
          city: r.city
        }))
      }

      return options
    })
    .catch((err) => console.error(err))
}

/**
 * Returns array of select options from the hotels locations api.
 *
 * @param {String} input Text string to search for
 * @param {String} language Language in 'xx' format
 */
export function getHotelsLocations(input) {
  let options = []
  const lang = 'es'

  if (input.length < 3) return new Promise((resolve) => resolve(options))

  return axios({
    method: 'GET',
    url: `${Config.endpoints.autocompleteHotels}?text=${input}&lang=${lang}`,
    headers: { 'Content-Type': 'application/json' },
  })
    .then(({ data }) => {
      const locations = data
      if (locations && locations.length) {
        options = locations.map((location) => ({
          value: {
            label: location.label,
            id: location.id,
            city: location.city_name,
            lat: location.latitude,
            lon: location.longitude,
            type: location.type,
            country: (location.country || '').toUpperCase(),
          },
          label: location.label,
        }))
      }
      return options
    })
    .catch((err) => console.error(err))
}

export function redirectUserToChannel(channelName) {
  let channelUrl = null

  const channelId = Config.auth[channelName] ? Config.auth[channelName].botId : null

  if (!channelId) return

  switch (channelName) {
    case 'whatsapp':
      channelUrl = `https://wa.me/${channelId}?text=start`
      break
    case 'telegram':
      channelUrl = `https://telegram.me/${channelId}?start=start`
      break
    case 'instagram':
      channelUrl = `https://www.instagram.com${channelId}?ref=start`
      break
    case 'facebook':
      channelUrl = `https://m.me/${channelId}?ref=start`
      break
    case 'sms':
      channelUrl = `sms:${channelId}`
      break
    default:
      return
  }

  if (channelUrl !== null) return window.open(channelUrl, '_blank')
}

export function generateLinkToBot(channel, company) {
  switch (channel.key) {
    case 'sms':
      return 'sms:+' + channel.value.botId
    case 'whatsapp':
      return 'https://wa.me/' + channel.value.botId
    case 'telegram':
      return 'https://t.me/' + channel.value.botId
    case 'instagram':
      return 'https://www.instagram.com/' + channel.value.botNameIG
    case 'facebook':
      return 'https://m.me/' + channel.value.botId
    case 'android':
      return `https://play.google.com/store/apps/details?id=${channel.app.bundle}`
    case 'ios':
      return `https://apps.apple.com/app/${channel.app.bundle}/id${channel.app.storeId}`
    case 'chatweb':
      return `${DNS_URLS.chatStandalone}/${company.name.replace(/ +/g, "")}/${channel.value.botId}`
    default:
      return null
  }
}

export function getBotId(channel) {
  if (!channel.value.botId && !channel.value.botName) return null
  if (channel?.hub === 'chatmail') return channel.value.name || channel.value.email
  switch (channel.key) {
    case 'sms':
      return '+' + channel.value.botId
    case 'whatsapp':
      return '+' + channel.value.botId
    case 'telegram':
      return '@' + channel.value.botId
    case 'instagram':
      return channel.value.botNameIG || channel.value.botId
    case 'facebook':
      return channel.value.botName || channel.value.botId
    case 'android':
    case 'ios':
      return channel.value.name || channel.app.bundle || channel.value.botId
    case 'chatweb':
    case 'wall':
      return channel.value.name || channel.value.botName || channel.value.botId
    default:
      return null
  }
}

export function getBotWidget(channel, company, icon, size, email) {
  let pathImg = `/widgets/${channel.key}`

  if (email) {
    pathImg += `-icon${!icon ? '-text': ''}`

    switch(size) {
      case 'small':
        pathImg += '-small'
        break
      case 'medium':
        pathImg += '-medium'
        break
      case 'normal':
      default:
        pathImg += ''
        break
    }

    pathImg += '.png'
  } else {
    pathImg += `${icon ? '-icon' : '-text'}.svg`
  }

  const imgSource = getStaticImage(pathImg)

  switch (channel.key) {
    case 'sms':
      return `<a target="_blank" href="sms:+${channel.value.botId}" style="background-color: transparent !important"><img src="${imgSource}" border="0" alt="nixi1 - sms link" style="max-width: 100%;"/></a>`
    case 'whatsapp':
      return `<a target="_blank" href="https://wa.me/${channel.value.botId}" style="background-color: transparent !important"><img src="${imgSource}" alt="nixi1 - whatsapp link" style="max-width: 100%;"/></a>`
    case 'telegram':
      return `<a target="_blank" href="https://telegram.me/${channel.value.botId}" style="background-color: transparent !important"><img src="${imgSource}" alt="nixi1 - telegram link" style="max-width: 100%;"/></a>`
    case 'facebook':
      return `<a target="_blank" href="https://www.messenger.com/t/${channel.value.botId}" style="background-color: transparent !important"><img src="${imgSource}" alt="nixi1 - facebook link" style="max-width: 100%;"/></a>`
    case 'instagram':
      return `<a target="_blank" href="https://www.instagram.com/${channel.value.botNameIG}" style="background-color: transparent !important"><img src="${imgSource}" alt="nixi1 - instagram link" style="max-width: 100%;"/></a>`
    case 'android':
      return `<a target="_blank" href="https://play.google.com/store/apps/details?id=${channel.app.bundle}" style="background-color: transparent !important"><img src="${imgSource}" alt="nixi1 - android link" style="max-width: 100%;"/></a>`
    case 'ios':
      return `<a target="_blank" href="https://apps.apple.com/app/${channel.app.bundle}/id${channel.app.storeId}" style="background-color: transparent !important"><img src="${imgSource}" alt="nixi1 - ios link" style="max-width: 100%;"/></a>`
    case 'chatweb':
      return `<a target="_blank" href="${DNS_URLS.chatStandalone}/${company.name.replace(/ +/g, "")}/${channel.value.botId}" style="background-color: transparent !important"><img src="${imgSource}" alt="nixi1 - ios link" style="max-width: 100%;"/></a>`
    case 'chatmail':
      return `<a target="_blank" href="${channel.url}" style="background-color: transparent !important"><img src="${imgSource}" border="0" alt="nixi1 - chatmail link" style="max-width: 100%;"/></a>`
    default:
      return null
  }
}

export function getBotWidgetFloating(channel, company, icon) {
  let urlImage = `/widgets/${channel.key}${icon ? '-icon' : '-text'}.svg`
  const imgSource = getStaticImage(urlImage)
  let link = ''
  switch (channel.key) {
    case 'sms':
      link = `sms:+${channel.value.botId}`
      break
    case 'whatsapp':
      link = `https://wa.me/${channel.value.botId}`
      break
    case 'telegram':
      link = `https://telegram.me/${channel.value.botId}`
      break
    case 'facebook':
      link = `https://www.messenger.com/t/${channel.value.botId}`
      break
    case 'instagram':
      link = `https://www.instagram.com/${channel.value.botNameIG}`
      break
    case 'android':
      link = `https://play.google.com/store/apps/details?id=${channel.app.bundle}`
      break
    case 'ios':
      link = `https://apps.apple.com/app/${channel.app.bundle}/id${channel.app.storeId}`
      break
    case 'chatweb':
      link = `${DNS_URLS.chatStandalone}/${company.name.replace(/ +/g, "")}/${channel.value.botId}`
      break
    case 'chatmail':
      link = `${channel.url}`
      break
    default:
      link = null
      break
  }
  const fragment = `<div onClick="window.open('${link}', '${company.name}', 'height=450,width=450,modal=yes,alwaysRaised=yes')">
                      <img src="${imgSource}" border="0" alt="link" style="max-width: 100%;position: fixed !important; bottom: 15px !important; right: 15px !important; cursor:pointer !important; z-index: 999999!important;"/>
                    </div>`
  return fragment

}

export function getChatWebScript(channel) {
  return `<script src="${DNS_URLS.chatwebScript}" async data-nxchanneltoken="${channel.value.botId}"></script>`
}

export function getTrialDaysLeft(trialAt) {
  const dateDiff = +new Date(trialAt) - Date.now()

  return Math.floor(dateDiff / 86400000)
}

/**
 * Given a string in format HH:MM it returs the number of minutes.
 * @param {String} hour
 */
export function hourStringToMinutes(hour) {
  if (!hour) return null
  let [hours, minutes] = hour.split(':')
  let totalMinutes = parseInt(hours, 10) * 60 + parseInt(minutes, 10)
  return totalMinutes
}

/**
 * Given a number of minutes, it returs an hour in HH:MM format.
 * @param {Number} num Minutes of the day, between 0 and 1439.
 */
export function minutesToTimeString(num) {
  let hours = Math.floor(num / 60)
  let minutes = num % 60

  if (hours < 10) hours = '0' + hours
  if (minutes < 10) minutes = '0' + minutes

  let timeString = `${hours}:${minutes}`

  return timeString
}

/**
 * Returns the time (HH:MM) given a date.
 *
 * @param date Date from where to get the time.
 */
export function getFlightDateTime(date) {
  return /[0-9]{2}:[0-9]{2}/.exec(date)[0]
}

/**
 * Returns a string of the total travel time in format XXh YYm
 * @param travelTime Total travel time in minutes
 * @returns String
 */
export function getFormatTravelTime(travelTime) {
  const totalTimeInHours = (travelTime / 60).toFixed(2)
  const totalHours = Math.floor(totalTimeInHours)
  const totalMinutesLeft = Math.round((totalTimeInHours - totalHours) * 60)

  return totalHours + 'h' + (totalMinutesLeft > 0 ? ' ' + totalMinutesLeft + 'm' : '')
}

/**
 * Returns the time between two hours in minutes.
 * @param startingHour The starting hour
 * @param endingHour The end hour
 * @param timeZoneHours Timezone changing hours
 */
export function calculateTimeBetweenHours(startingHour, endingHour, timeZoneHours = 0) {
  startingHour = startingHour.split(':')
  endingHour = endingHour.split(':')

  const startingDate = new Date().setHours(parseInt(startingHour[0], 10), parseInt(startingHour[1], 10))
  let endingDate = new Date().setHours(parseInt(endingHour[0], 10), parseInt(endingHour[1], 10))

  if (startingDate > endingDate) {
    endingDate = new Date(endingDate)
    let endingDateDay = endingDate.getDate() + 1
    endingDate.setDate(endingDateDay)
  }

  return Math.abs((startingDate - endingDate) / 1000 / 60 + timeZoneHours * 60)
}

/**
 * Expires a cookie given its name.
 *
 * @param {String} cookieName Cookie to expire
 */
export const expireCookie = (cookieName) => {
  document.cookie = cookieName + '=;expires=Thu, 01 Jan 1970 00:00:01 GMT;'
}

/**
 * Validate capabilities for channel.
 *
 * @param {Object} textPreview Objeto to review
 * @param {String} channel Channel
 * @param {Boolean} isChat Marketing / Conversations
 */
export const allowTemplate = (textPreview, channel, isChat) => {
  const hasImage = textPreview.attachments && textPreview.attachments.length > 0 && textPreview.attachments[0].mimeType === 'image'
  const hasVideo = textPreview.attachments && textPreview.attachments.length > 0 && textPreview.attachments[0].mimeType === 'video'
  const hasText = textPreview.content !== ''
  const hasButton = textPreview.buttons && textPreview.buttons.length > 0
  const countButton = textPreview.buttons ? textPreview.buttons.length : 0
  if (channel === 'facebook' || channel === 'instagram') {
    if (!isChat) {
      let toSave = false
      if (hasImage) toSave = hasText && hasButton
      else if (hasButton) toSave = hasText
      else toSave = hasText
      return toSave
    } else {
      let toSave = false
      if (hasImage) {
        if (countButton === 1 && !hasText) toSave = true
        else if (countButton === 1 && hasText) toSave = false
        else if (countButton > 1 && hasText) toSave = true
        else if (countButton > 1 && !hasText) toSave = false
        else if (hasText) toSave = true
        else toSave = true
      }
      else if (hasVideo) {
        toSave = false
        if (countButton <= 1 && !hasText) toSave = true
      }
      else if (hasButton) toSave = hasText
      else toSave = hasText
      return toSave
    }
  } else return true
}

export const detectUrlINText = async (text) => {
  //ALLOW HTTPS AND MORE
  //const regex = /[-a-zA-Z0-9@:%._\+~#=/]{1,256}\:[-a-zA-Z0-9@:%._\+~#=/]{1,256}\.[-a-zA-Z0-9@:%._\+~#=/?]{1,256}\b/gi
  //ONLY HTTP & HTTPS
  const regex = /https?:\/\/[-a-zA-Z0-9@:%._\+~#=/]{1,256}\.[-a-zA-Z0-9@:%._\+~#=/?]{1,256}\b/gi
  let m
  let urls = []
  //let content = text
  while ((m = regex.exec(text)) !== null) {
    // This is necessary to avoid infinite loops with zero-width matches
    if (m.index === regex.lastIndex) {
      regex.lastIndex++
    }
    m.forEach((match, groupIndex) => {
      urls.push(match)
    })
  }
  if (urls.length > 0) {
    const result = await shorten.shortenUrl({ urls })
    if (result.status === 200) {
      return result.data.result
    }
  }
  return null
}

/**
 * Generate QR.
 *
 * @param {String} link URL
 */
export const generateQR = async (dataForQRcode) => {
  return new Promise((resolve) => {
    const canvas = document.createElement('canvas')
    QRCode.toCanvas(
      canvas,
      dataForQRcode,
      {
        errorCorrectionLevel: 'H',
        // version: 7,
        color: {
          dark: '#000000',
          light: '#ffffff'
        },
        scale: 36,
        margin: 0
      }
    )

    const ctx = canvas.getContext("2d")
    const img = new Image()
    img.src = '/img/qr-logo-black-white.svg'
    img.onload = async function () {
      const center = (canvas.width - 350) / 2
      ctx.drawImage(img, center, center, 350, 350)

      // const a = document.createElement('a')
      // a.href = canvas.toDataURL("image/png")
      // a.download = 'poster-qr11.png'
      // a.click()

      return resolve(canvas.toDataURL("image/png"))
    }
  })
}

/**
 * Get timezones object
 *
 */
export function timezones() {
  const data = []
  rawTimeZones.forEach(it => {
    const gmt = it.rawFormat.substr(0, 6)
    const cities = it.mainCities.join(', ')
    data.push({ label: `(${gmt}) ${cities}`, value: it.name })
  })
  return data
}

export function currencies() {
  return [
    { label: 'AUD ($)', value: 'AUD' },
    { label: 'CAD (C$)', value: 'CAD' },
    { label: 'CHF', value: 'CHF' },
    { label: 'EUR (€)', value: 'EUR' },
    { label: 'GBP (£)', value: 'GBP' },
    { label: 'SGD (S$)', value: 'SGD' },
    { label: 'USD ($)', value: 'USD' },
  ]
}

export function staff() {
  return [
    { label: '1-20', value: '1-20' },
    { label: '21-50', value: '21-50' },
    { label: '51-200', value: '51-200' },
    { label: '201-500', value: '201-500' },
    { label: '501-1000', value: '501-1000' },
    { label: '1001-5000', value: '1001-5000' },
    { label: '5001-10000', value: '5001-10000' },
    { label: '10001+', value: '10001+' },
  ]
}

export const lang = (text = '', variables = {}) => text ? I18n.t(text, variables) : ''

/**
 * Remove html tags from string
 * @param {String} value html
 * @return String
*/
export const removeHtmlTags = (value = '') => {
  const regex = /(<([^>]+)>)/ig

  return value.replace(regex, "")
}

/**
 * Validate Shortcut for AV, a-z ñ 0-9 _ -
 * @param {String} value shortcut as string
 * @return String
*/
export const validateShorcutsAV = (value = '') => {
  const regex = /[a-z ñ 0-9_-]+/
  let str
  if ((str = regex.exec(value)) !== null) return (str)
  else return value
}

/**
 * Check if conversation is online
 * @param {Object} user Conversation user/ticket
 * @returns
 */
export const isConversationOnline = (user) => {
  let isOnline = true
  const channel = user?.channels?.[0]?.channel

  if (channel === 'whatsapp' || channel === 'facebook' || channel === 'instagram') {
    const lastMessageDate = user.lastMessageDate
    const currentDate = DateTime.now()
    const endDate = DateTime.fromISO(lastMessageDate).plus({ days: channel !== 'whatsapp' ? 7 : 1 })
    const duration = Interval.fromDateTimes(currentDate, endDate)
      .toDuration(['years', 'months', 'days', 'hours', 'minutes', 'seconds', 'milliseconds'])
      .toObject()

    const endedTime = duration.seconds === undefined

    isOnline = endedTime ? false : true
  }
  else {
    isOnline = user.isOnline
  }

  return isOnline
}

/**
 *
 * @param {string} inputString
 * @returns {string} outputString
 */
export const removeLastClosingPTag = (inputString) => {
  const regex = /<\/p>(?![\s\S]*<\/p>)/
  return inputString.replace(regex, '')
}

/**
 *
 * @param {string} inputString
 * @returns {string} outputString
 */

export const replaceWhitespaces = (inputString) => {
  return inputString.replace(/\s/, '_')               
}

/**
 *
 * @param {string} inputString
 * @returns {string} outputString
 */

export const removeSpecialCharacters = (inputString) => {
  return inputString.replace(/[^\w]/ig, '')               
}

   
